import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as MuiLab from "@mui/material";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { Helmet } from "react-helmet";
import classNames from "classnames";
import { TopMenu } from "./top-menu";
import { SidebarMenu } from "./sidebar-menu";
import { getViewType } from "redux/selector";
import { getUserUnsafe } from "redux/selector";
import { getIntegrationByType } from "redux/selector";
import { styles } from "./style";
import { AccountMenu } from "./account-menu";
import { QuickSearchDialog } from "./quick-search-dialog";
import { NotPermittedPage } from "component/page/not-permitted";
// @ts-ignore
import Favicon from "react-favicon";
import { MobileAppDialog } from "./mobile-app-dialog";
import { IntegrationType } from "type/integration-type";
import { urls } from "routes/urls";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";

interface Props extends WithStyles<typeof styles> {
	title: string;
	header?: React.ReactNode;
	iframe?: React.ReactNode;
	permitted?: boolean | null;
	fullScreen?: boolean;
	/**
	 * Hide the left sidebar only — the top nav bar stays. For pages that own their full width and
	 * are reached from the profile menu rather than the sidebar (MAX-4139). Distinct from the
	 * internal `hideNav`, which is iframe-embedding detection and hides both.
	 */
	hideSidebar?: boolean;
	/** Content max-width when `fullScreen` is false. Defaults to 1024. */
	contentMaxWidth?: number;
	children?: React.ReactNode;
}

const Component: React.FC<Props> = ({
	classes = {},
	children,
	permitted, 
	title, 
	header, 
	iframe, 
	fullScreen = true,
	hideSidebar = false,
	contentMaxWidth,
}) => {
	// Redux selectors
	const user = useSelector((state: RootState) => getUserUnsafe(state));
	const viewType = useSelector((state: RootState) => getViewType(state));
	const emailCalenderIntegration = useSelector((state: RootState) => 
		getIntegrationByType(state, IntegrationType.EMAIL_CALENDAR)
	);

	// Local state
	const [toggleLeft, setToggleLeft] = useState(false);
	const [quickSearchOpen, setQuickSearchOpen] = useState(false);
	const [hideNav, setHideNav] = useState(false);

	// Refs
	const styleElementRef = useRef<HTMLStyleElement | null>(null);

	// Memoized computed values
	const resellerCss = useMemo(() => {
		if (user && user.reseller.css) {
			let result = user.reseller.css;
			result = result.replace(/#ih-cpnav-/g, ".ihf-");
			return result;
		}
		return "";
	}, [user?.reseller.css]);

	// Callback functions
	const removeResellerCSS = useCallback(() => {
		if (styleElementRef.current) {
			styleElementRef.current.remove();
			styleElementRef.current = null;
		}
	}, []);

	const applyResellerCSS = useCallback(() => {
		const shouldShowCSS = viewType.client;

		// Remove existing CSS first
		removeResellerCSS();

		if (user?.reseller.css && shouldShowCSS) {
			// Create and append new style element
			const style = document.createElement('style');
			style.type = 'text/css';
			style.id = 'reseller-css';
			style.textContent = resellerCss;
			document.head.appendChild(style);
			styleElementRef.current = style;
		}
	}, [user?.reseller.css, viewType.client, resellerCss, removeResellerCSS]);

	const handleViewChange = useCallback(() => {
		const shouldShowCSS = viewType.client;

		if (shouldShowCSS) {
			applyResellerCSS();
		} else {
			removeResellerCSS();
		}
	}, [viewType.client, applyResellerCSS, removeResellerCSS]);

	const openSearch = useCallback((event: KeyboardEvent) => {
		if (event.ctrlKey && event.key === "f" && user && !user.permissions.oneCrm) {
			event.preventDefault();
			setQuickSearchOpen(prev => !prev);
		}
	}, [user?.permissions.oneCrm]);

	const toggleLeftSidebar = useCallback(() => {
		setToggleLeft(prev => !prev);
	}, []);

	// Effects
	useEffect(() => {
		document.addEventListener("keydown", openSearch);
		applyResellerCSS();

		const isInIframe = window !== window.parent;
		const trustedReferrer =
			document.referrer.startsWith("https://manage.wix.com") ||
			document.referrer.startsWith("https://editor.wix.com") || 
			document.referrer.includes("idxhome.com");

		if (isInIframe && !trustedReferrer) {
			setHideNav(true);
		}

		return () => {
			document.removeEventListener("keydown", openSearch);
			removeResellerCSS();
		};
	}, [openSearch, applyResellerCSS, removeResellerCSS]);

	// Effect for handling view type changes
	useEffect(() => {
		handleViewChange();
	}, [handleViewChange]);

	// Effect for handling user/reseller CSS changes
	useEffect(() => {
		applyResellerCSS();
	}, [applyResellerCSS]);

	if (!user) {
		throw new Error();
	}
	if (permitted === false) {
		return <NotPermittedPage />;
	}

	return (
		<>
			<Helmet title={`${title} | ${user.reseller.name} Control Panel`} />
			<Favicon url={[user.reseller.direct ? "favicon.ico" : "blank.ico"]} />
			<MobileAppDialog />
			{quickSearchOpen && (
				<QuickSearchDialog
					open={quickSearchOpen}
					onClose={() => setQuickSearchOpen(false)}
				/>
			)}

			<div>
				{emailCalenderIntegration && emailCalenderIntegration.errored && (
					<MuiLab.Alert severity="error">
						Uh oh! Your {emailCalenderIntegration.label} integration is not working correctly.
						{" "}
						<Mui.Link component={RouterLinkWrapper} to={urls.settingsIntegrations}>
							Update it here.
						</Mui.Link>
					</MuiLab.Alert>
				)}

				<div className={classes.layout}>
					{!hideNav && !hideSidebar && (
						<div
							className={classNames(
								classes.left,
								toggleLeft ? "show" : null,
							)}
						>
							<div
								className={classes.leftTop}
								style={user.reseller.logo && !user.reseller.direct ? {
									backgroundColor: "#fff",
								} : {}}
							>
								<div className={classes.logoWrapper}>
									{user.controlPanelUrl ? (
										<img className={classes.logo} src={user.controlPanelUrl} alt="" />
									) : user.reseller.logo ? (
										<img className={classNames(classes.logoReseller)} src={user.reseller.logo} alt="" />
									) : (
										<div className={classes.logoMissing}>{user.reseller.name}</div>
									)}
								</div>
								<Mui.Hidden mdUp>
									<Mui.IconButton
										size="small"
										color="inherit"
										onClick={toggleLeftSidebar}
									>
										<FeatherIcon>
											<Icons.ArrowLeft fontSize="small" />
										</FeatherIcon>
									</Mui.IconButton>
								</Mui.Hidden>
							</div>
							<div className={classes.leftBottom}>
								<SidebarMenu user={user} viewType={viewType} />
							</div>
							<div className="backdrop" onClick={toggleLeftSidebar} />
						</div>
					)}
					<div className={classes.right}>
						{!hideNav && (
							<Mui.AppBar position="sticky" className={classes.navBar} elevation={0}>
								<Mui.Toolbar>
									<Mui.Hidden mdUp>
										<Mui.IconButton onClick={toggleLeftSidebar}>
											<FeatherIcon>
												<Icons.Menu />
											</FeatherIcon>
										</Mui.IconButton>
									</Mui.Hidden>
									<div className={classes.topMenuWrapper}>
										<Mui.Hidden smDown>
											<TopMenu user={user} />
										</Mui.Hidden>
									</div>
									<div className={classes.flexGrow} />
									{!user.permissions.oneCrm && (
										<Mui.IconButton
											onClick={() => setQuickSearchOpen(true)}
										>
											<FeatherIcon>
												<Icons.Search />
											</FeatherIcon>
										</Mui.IconButton>
									)}
									<AccountMenu />
								</Mui.Toolbar>
							</Mui.AppBar>
						)}

						<div className={classes.content}>
							{header && (
								<Mui.AppBar position="relative" className={classes.header} elevation={0}>
									<Mui.Toolbar className={classes.toolbar} style={{ minHeight: "auto" }}>
										<div className={classes.headerContent}>
											{header}
										</div>
									</Mui.Toolbar>
								</Mui.AppBar>
							)}

							{iframe && <div className={classes.iframeContainer}>{iframe}</div>}

							{children && <div className={classNames(
								classes.mainContainer,
								(!fullScreen) ? classes.fixedWidthContent : null
							)}>
								{fullScreen && children}
								{!fullScreen && <div
									className={classes.fixedWidthChild}
									style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}
								>
									{children}
								</div>}
							</div>}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export const DashboardLayout = withStyles(Component, styles);